import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@components/Button";
import { BinIcon, CrossIcon } from "@components/icons";
import { Modal } from "@components/Modal/Modal";
import { useModal } from "@hooks/useModal";
import { useTranslationArray } from "@hooks/useTranslationArray";
import { type IUseModalProps } from "@store/modal/modal";
import { useDeleteJointBillServiceMutation } from "@services/jointBillServices/mutations";
import { sanitizeHtml } from "@utils/html";

interface IRemoveHouseModalProps extends IUseModalProps {
  jointBillServiceId: number;
}

export const DeleteHouseModal: React.FC<IRemoveHouseModalProps> = (props) => {
  const { jointBillServiceId, ...rest } = props;
  const { t } = useTranslation();
  const modal = useModal(rest);
  const outcome = useTranslationArray("deleteHouseModal.outcome");

  const { mutateAsync, isPending } = useDeleteJointBillServiceMutation();

  const onRemove = async () => {
    const response = await mutateAsync({
      jointBillServiceId: jointBillServiceId,
    }).catch(() => {
      toast.error(t("toast.error"));
    });

    if (response) {
      toast.success(t("deleteHouseModal.toast.success"));
      modal.close();
    }
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{ icon: BinIcon, color: "red" }}
      title={t("deleteHouseModal.title")}
      subtitle={t("deleteHouseModal.subtitle")}
      actionButtons={
        <React.Fragment>
          <Button className="w-full" variant="secondary" onClick={modal.close}>
            {t("deleteHouseModal.buttons.cancel")}
          </Button>
          <Button className="w-full" onClick={onRemove} isLoading={isPending}>
            {t("deleteHouseModal.buttons.submit")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-3">
        {outcome?.map((item) => (
          <div className="flex items-center gap-2">
            <CrossIcon className="size-4 min-w-4 text-base-red" />
            <span
              className="text-sm text-grey-dark"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(item) }}
            />
          </div>
        ))}
      </div>
    </Modal>
  );
};
